import React from "react";

import {
  ArrowForwardRounded,
  ExpandMoreRounded,
  MessageRounded,
  NotificationsRounded,
  OpenInNewRounded,
  PhotoLibraryRounded,
  StorefrontRounded,
} from "@mui/icons-material";
import { Badge, Box, ButtonBase, Collapse, Divider } from "@mui/material";

import useMainState from "../../../../../../../../config/context/mainStateContext";
import useModal from "../../../../../../../../config/context/modalContext";
import { GreyPallet } from "../../../../../../../../config/styles";
import { UtilComponents } from "../../../../../../../../config/util/utilComponents";

import ExpandMore from "../../../../../../../../components/ExpandMore";
import MainBorderButton from "../../../../../../../../components/MainBorderButton";
import MainSolidButton from "../../../../../../../../components/MainSolidButton";
import MainText from "../../../../../../../../components/MainText";
import FoodItem from "../FoodItem";

import { useNavigate } from "react-router-dom";
import { CafeteriaFoodItemType, CafeteriaType } from "../../../../../../../../types/database";

type CafeteriaCardType = {
  item: CafeteriaType;
};

export default function CafeteriaCard({ item }: CafeteriaCardType) {
  const navigate = useNavigate();

  const { showImage } = useModal();
  const { openLink } = useMainState();

  const [showDetail, setShowDetail] = React.useState(false);

  const [resume, setResume] = React.useState<{ [key: string]: any }>({});

  const handleAddItem = (itemToAdd: CafeteriaFoodItemType) => {
    const newState = { ...resume };

    if (itemToAdd.id in newState) {
      newState[itemToAdd.id].amount++;
    } else {
      newState[itemToAdd.id] = { ...itemToAdd, amount: 1 };
    }

    setResume(newState);
  };

  const handleRemoveItem = (itemToRem: CafeteriaFoodItemType) => {
    const newState = { ...resume };

    if (itemToRem.id in newState) {
      if (newState[itemToRem.id].amount < 2) {
        delete newState[itemToRem.id];
      } else {
        newState[itemToRem.id].amount--;
      }
    }

    setResume(newState);
  };

  React.useEffect(() => {}, []);

  return (
    <Badge
      badgeContent={item.isOpen ? "Aberto" : "Fechado"}
      color={item.isOpen ? "success" : "error"}
      sx={{ "& .MuiBadge-badge": { right: 30 }, width: "100%", opacity: item.isOpen ? 1 : 0.6 }}
    >
      <Box
        width={"100%"}
        sx={{ backgroundColor: GreyPallet.Light, borderRadius: "15px", p: 2, mb: 2 }}
      >
        <Box
          sx={{
            width: "100%",
            alignItems: "center",
            display: "flex",
            gap: 1,
          }}
        >
          <ButtonBase
            onClick={() => !!item.image && showImage(item.image.resource)}
            sx={{
              width: "60px",
              height: "60px",
              backgroundColor: GreyPallet.LightGhost,
              borderRadius: "5px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: GreyPallet.Regular,
              overflow: "hidden",
            }}
          >
            {item.image ? (
              <img
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                src={item.image.resource}
              />
            ) : (
              <StorefrontRounded sx={{ width: "32px", height: "32px" }} />
            )}
          </ButtonBase>

          <Box sx={{ flex: 1 }}>
            <MainText>{item.name}</MainText>
            <MainText color="regular" size="caption">
              {item.description}
            </MainText>
          </Box>

          <ExpandMore
            expand={showDetail}
            onClick={() => setShowDetail(!showDetail)}
            aria-expanded={showDetail}
          >
            <ExpandMoreRounded />
          </ExpandMore>
        </Box>

        <Collapse in={showDetail} timeout="auto" unmountOnExit sx={{}}>
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
            mt={2}
            mb={1}
            flexWrap={"wrap"}
          >
            {!!item.phone && (
              <Box flex={1} textAlign={"center"}>
                <MainText size="tiny" color="regular">
                  WhatsApp
                </MainText>
                <MainBorderButton
                  icon={<MessageRounded />}
                  small
                  color="default"
                  onClick={() =>
                    openLink(
                      `https://wa.me/55${
                        item?.phone || ""
                      }?text=Ol%C3%A1.+Vi+seus+produtos+no+Circular+UFRN`
                    )
                  }
                />
              </Box>
            )}

            {!!item.site && (
              <Box flex={1} textAlign={"center"}>
                <MainText size="tiny" color="regular">
                  Site
                </MainText>
                <MainBorderButton
                  icon={<OpenInNewRounded />}
                  small
                  color="default"
                  onClick={() => openLink(String(item?.site))}
                />
              </Box>
            )}

            {!!item.instagram && (
              <Box flex={1} textAlign={"center"}>
                <MainText size="tiny" color="regular">
                  Instagram
                </MainText>
                <MainBorderButton
                  icon={<PhotoLibraryRounded />}
                  small
                  color="default"
                  onClick={() => openLink(`https://www.instagram.com/${item.instagram}/`)}
                />
              </Box>
            )}

            <Box flex={1} textAlign={"center"}>
              <MainText size="tiny" color="regular">
                Notificações
              </MainText>
              <MainBorderButton icon={<NotificationsRounded />} small color="default" />
            </Box>
          </Box>

          <Box mb={2}>
            <MainText size="tiny" color="regular">
              Ative as notificações para receber avisos sempre que este vendedor estiver aberto.
            </MainText>
          </Box>

          {item.menu.map((menuItem) => (
            <FoodItem
              key={menuItem.id}
              item={menuItem}
              amount={resume[menuItem.id]?.amount || 0}
              addItem={handleAddItem}
              removeItem={handleRemoveItem}
            />
          ))}

          {Object.keys(resume).length > 0 && (
            <Box
              sx={{
                mt: 2,
                backgroundColor: GreyPallet.LightGhost,
                borderRadius: "15px",
                p: 2,
              }}
            >
              <MainText size="caption">Resumo</MainText>

              <Box sx={{ textAlign: "right" }}>
                {Object.keys(resume).map((resumeItemKey: any) => (
                  <Box mb={1} key={resumeItemKey}>
                    <MainText size="caption" color="regular">
                      {resume[resumeItemKey].name}
                    </MainText>
                    <MainText size="caption" color="regular">
                      <span style={{ whiteSpace: "nowrap" }}>
                        x{resume[resumeItemKey].amount}{" "}
                        {UtilComponents.toPrice(resume[resumeItemKey].price)}
                      </span>
                    </MainText>
                  </Box>
                ))}

                {/* <Box mb={1}>
                <MainText size="caption" color="regular">
                  Quiche de mussarela com tomate
                </MainText>
                <MainText size="caption" color="regular">
                  <span style={{ whiteSpace: "nowrap" }}>x1 R$ 3,50</span>
                </MainText>
              </Box> */}

                <Divider sx={{ my: 1 }} />

                <MainText>
                  Total:{" "}
                  {UtilComponents.toPrice(
                    Object.keys(resume).reduce(
                      (p, key) => p + resume[key].amount * resume[key].price,
                      0
                    )
                  )}
                </MainText>
              </Box>
            </Box>
          )}

          {/* <ButtonBase
            sx={{
              mb: 1,
              mt: 2,
              display: "flex",
              justifyContent: "content-between",
              alignItems: "center",
              gap: 1,
              width: "100%",
              textAlign: "left",
              borderRadius: "15px",
              backgroundColor: GreyPallet.LightGhost,
              px: 2,
              py: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
                gap: 1,

                textAlign: "left",
                width: "100%",
              }}
            >
              <LocationOnRounded />
              <Box>
                <MainText size="small" color="regular">
                  Local de retirada:
                </MainText>
                <MainText size="body">{item.locationText}</MainText>
              </Box>
            </Box>

            <ArrowForwardRounded />
          </ButtonBase> */}

          {/* <Box mb={1}>
            <MainText color="regular" size="tiny">
              *OBS: Tenha em mente que alguns itens podem não estar mais disponíveis no momento da
              compra. Aguarde a confirmação do vendedor para fazer a retirada.
            </MainText>
          </Box> */}

          {/* {item.config.enableReserve && item.isOpen && Object.keys(resume).length > 0 && (
            <MainSolidButton small text="Reservar" icon={<ReceiptLongRounded />} />
          )} */}

          {/* <MainSolidButton small text="Pagar" icon={<PixRounded />} /> */}

          <Box mt={2}>
            <MainSolidButton
              onClick={() => navigate("/home/menu/" + item?.site)}
              small
              text="Pedir"
              icon={<ArrowForwardRounded />}
            />
          </Box>
        </Collapse>
      </Box>
    </Badge>
  );
}
